/**
 * Where the Value figure came from, deal by deal.
 *
 * A total is a claim, and the honest thing a total can do is show its
 * working. This one has to more than most: it adds up three deals out of
 * forty-five, and the forty-two it leaves out are left out for three
 * different reasons. "AED 1,048" with no way through to *which three* is
 * the kind of figure people learn to distrust and then ignore.
 *
 * So the rows are grouped by the reason rather than listed flat, each group
 * says what it means in a sentence, and every row carries the client and the
 * plan the figure was read from - because the answer to "why is this one
 * blank" is always a fact about that client's record, not about this page.
 */
import { useApp } from '../../store/AppContext.jsx';
import Icon from '../../lib/icons.jsx';
import Sheet from '../Sheet.jsx';
import { Avatar, Pill, Dash, EmptyState } from '../Primitives.jsx';
import { dealsOf, clientOfDeal, dealValue } from '../../domain/crm.js';
import { parseDate, periodLabel } from '../../lib/dates.js';

/*
 * The four ways a deal ends up in this list, in the order they are useful:
 * what was counted first, then what was left out and why.
 */
const GROUPS = [
  {
    key: 'counted',
    title: 'Counted',
    note: 'The client record carries an amount. Each is shown as it is billed and counted as what it comes to in a month.',
    icon: 'check',
    tone: 'g-green'
  },
  {
    key: 'free',
    title: 'Free plan',
    note: 'Worth nothing, and that is a fact rather than a gap - a Free plan has no price.',
    icon: 'star',
    tone: 'g-cyan'
  },
  {
    key: 'unpriced',
    title: 'No amount on the client record',
    note: 'On a paid plan, so they are worth something. Fill the amount in on the clients app and it appears here.',
    icon: 'alert',
    tone: 'g-amber'
  },
  {
    key: 'forecast',
    title: 'Not a client yet',
    note: 'Nobody has become a customer here and nobody has forecast a figure, so there is nothing to add up.',
    icon: 'zap',
    tone: 'g-pink'
  }
];

export default function DealValueView({ scope = 'all', onClose }) {
  const { crm, rows, fmtMoney, fmtDate, setSheet } = useApp();

  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const board = dealsOf(crm).filter(d => {
    if (scope !== 'month') return true;
    if (d.stage !== 'Won') return false;
    const c = parseDate(d.close);
    return c && c >= monthStart;
  });

  /* Sort each group by what it is worth, then by name: the biggest figure is
     the one somebody opened this to find. */
  const bucket = { counted: [], free: [], unpriced: [], forecast: [] };
  board.forEach(d => {
    const client = clientOfDeal(rows, d);
    const v = dealValue(rows, d);
    const row = { deal: d, client, v };
    if (!client) bucket.forecast.push(row);
    else if (v.free) bucket.free.push(row);
    else if (v.known) bucket.counted.push(row);
    else bucket.unpriced.push(row);
  });
  /* Sorted on the monthly rate, not the stated amount: by the amount, an
     annual 950 outranks a monthly 200 that is worth more than twice as
     much over a year. */
  Object.values(bucket).forEach(list => list.sort(
    (a, b) => b.v.monthly - a.v.monthly || String(a.deal.title).localeCompare(String(b.deal.title))
  ));

  const total = bucket.counted.reduce((t, r) => t + r.v.monthly, 0);
  const yearly = bucket.counted.filter(r => r.v.cycle && !/^monthly$/i.test(r.v.cycle)).length;
  const left = board.length - bucket.counted.length;

  return (
    <Sheet
      icon="chart"
      tone="g-green"
      title={scope === 'month' ? 'Won this month, in full' : 'Where the value comes from'}
      onClose={onClose}
      closeLabel="Close"
      wide
      bodyClass="dvv"
      hero={(
        <div className="dvvhero">
          <div>
            <b>{fmtMoney(total)}<em>/mo</em></b>
            <small>
              read from {bucket.counted.length} client record{bucket.counted.length === 1 ? '' : 's'}
              {/* Said out loud, because the hero is not the sum of the
                  numbers in the rows below it and somebody will check. */}
              {yearly ? ` · ${yearly} billed for longer than a month, divided down` : ''}
              {scope === 'month' ? ' · won this month' : ''}
            </small>
          </div>
          {/* The figure is only half the answer; the other half is how much
              of the board it could not speak for. */}
          <div className="dvvsplit">
            {GROUPS.slice(1).map(g => (
              bucket[g.key].length ? (
                <span key={g.key} className={`dvvchip ${g.tone}`}>
                  <b>{bucket[g.key].length}</b> {g.title.toLowerCase()}
                </span>
              ) : null
            ))}
          </div>
        </div>
      )}
    >
      {!board.length ? (
        <div style={{ gridColumn: '1/-1' }}>
          <EmptyState icon="card" tone="g-slate" title="Nothing on the board">
            {scope === 'month' ? 'No deal was won this month.' : 'No deals to add up.'}
          </EmptyState>
        </div>
      ) : null}

      {GROUPS.map(g => {
        const list = bucket[g.key];
        if (!list.length) return null;
        const sum = list.reduce((t, r) => t + r.v.monthly, 0);

        return (
          <section className="dvvgroup" key={g.key}>
            <header>
              <span className={`ic ${g.tone}`}><Icon name={g.icon} /></span>
              <div>
                <b>{g.title}<span className="dvvn">{list.length}</span></b>
                <small>{g.note}</small>
              </div>
              {g.key === 'counted' ? <span className="dvvsum">{fmtMoney(sum)}/mo</span> : null}
            </header>

            <div className="dvvrows">
              {list.map(({ deal, client, v }) => (
                <button
                  type="button"
                  className="dvvrow"
                  key={deal.id}
                  onClick={() => setSheet({ kind: 'dealView', id: deal.id })}
                >
                  <Avatar name={deal.title} />
                  <div className="dvvwho">
                    <b>{deal.title}</b>
                    {/* Which client the figure was read from, said plainly:
                        a deal and the client it became often have different
                        names, and that is the join being shown. */}
                    <small>
                      {client
                        ? <>{client.name}{client.email ? ` · ${client.email}` : ''}</>
                        : (deal.leadEmail || 'no address on the row')}
                    </small>
                  </div>

                  <div className="dvvplan">
                    {client ? (
                      <>
                        <Pill tone={v.free ? 'gr-slate' : 'gr-violet'}>{client.plan || 'no plan'}</Pill>
                        {client.cycle ? <small>{client.cycle}</small> : null}
                      </>
                    ) : <Dash />}
                  </div>

                  <div className="dvvwhen">
                    {deal.close ? fmtDate(parseDate(deal.close)) : <Dash />}
                    <small>{deal.stage}</small>
                  </div>

                  {/* The invoice as it is written, with the monthly rate
                      under it wherever the two differ — so the row can be
                      checked against the clients app and still be seen to
                      belong to the total above. */}
                  <div className={`dvvamt${v.known && !v.free ? '' : ' off'}`}>
                    {v.free ? 'Free' : v.known ? (
                      <>
                        {fmtMoney(v.amount)}<span className="dvvper">{periodLabel(v.cycle)}</span>
                        {v.monthly !== v.amount
                          ? <small>{fmtMoney(v.monthly)}/mo</small>
                          : null}
                      </>
                    ) : '—'}
                  </div>
                </button>
              ))}
            </div>
          </section>
        );
      })}

      {left ? (
        <p className="dvvfoot">
          <Icon name="alert" />
          {left} of {board.length} could not be counted. The amount lives on the
          client record in the clients app, and this page reads it rather than
          keeping its own copy — so filling one in there shows up here at once.
        </p>
      ) : null}
    </Sheet>
  );
}
